import { useContext, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { SignaturePage } from "./ui"
import { SignatureFilter } from "./ui.filter"
import { IFilterSignature, ISignaturePage, ISignatureStatuses } from "./types"

import { useValue } from "@hooks/useValue"
import { useDelayedInput } from "@hooks/useDelayedInput"

import { useDict } from "@api/dict/state"
import { useContractsSignature } from "@api/signature/state"
import { FilterParametersType } from "@api/signature/state.types"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"
import { CardSignatureContainer, ICardSignatureContainer } from "@smart/cardSignature"
import realtimeService from "@services/realtime.service"

// ================================================================
// ================================================================

/** Контейнер страницы - Договора на подпись */
export const SignatureContainer = () => {
    const notify = useContext(NotificationContext)

    const filterIsOpen = useValue<boolean>(false)
    const filterIsActivate = useValue<boolean>(false)
    const filterSignatureStatuses = useValue<ISignatureStatuses[]>([])
    const filterDateNextCallFrom = useValue<string>('')
    const filterDateNextCallBefore = useValue<string>('')

    /** Таймер, для автоматического обновления основного списка */
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)

    /** Пагинация - Изменение номера страницы */
    const paginationPageNumber = useValue<number>(1)

    /** Окно с анкетой заявки */
    const cardSignature = useValue<ICardSignatureContainer>({isOpen: false})


    // Хранилище страницы - Заявки
    const { 
        isLoadingPage, primaryList, primaryListPageSize, 
        primaryListTotalCount, primaryListNew, primaryListDelayedCall, 
        primaryListExecution, primaryListCanceled, primaryListNotClient,
        primaryListIgnore,

        actionGetPrimaryList, actionPostTgSignature, clearStateContractsSignature,
    } = useContractsSignature(useShallow((state) => ({
        isLoadingPage: state.primaryList.isLoading,

        primaryList: state.primaryList.items,
        primaryListPageSize: state.primaryList.pageSize,
        primaryListTotalCount: state.primaryList.totalCount,

        primaryListNew: state.primaryList.new,
        primaryListDelayedCall: state.primaryList.delayedCall,
        primaryListExecution: state.primaryList.execution,
        primaryListCanceled: state.primaryList.canceled,
        primaryListNotClient: state.primaryList.notClient,
        primaryListIgnore: state.primaryList.ignore,
        primaryListRefund: state.primaryList.refund,

        actionGetPrimaryList: state.actionGetPrimaryList,
        actionPostTgSignature: state.actionPostTgSignature,
        clearStateContractsSignature: state.clearStateContractsSignature,
    })))

    // Хранилище справочники
    const dictState = useDict(useShallow((state) => state))
    
    /** Загрузка первичных данных */
    const loadPrimaryData = (search: string, isShowLoading: boolean = true) => {
        actionGetPrimaryList(paginationPageNumber.value, search, filtersRef.current, isShowLoading).then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                if (res.status === 403) {
                    clearStateContractsSignature()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        })
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 1000, (val) => loadPrimaryData(val))

    const searchRef = useRef(txtSearch)

    const filtersRef = useRef<FilterParametersType>({
        statuses: "",
        dateNextCallFrom: "",
        dateNextCallBefore: "",
    })

    useEffect(() => {
        dictState.actionGetContractSignatureStatuses().then((res) => {
            filterSignatureStatuses.setValue(
                res.map((item) => ({
                    id: item.contractStatusCallCenterID,
                    name: item.contractStatusCallCenterName,
                    isSelected: false
                }))
            )    
        })

        return (() => {
            clearStateContractsSignature()

            setTxtSearch('')
            paginationPageNumber.setValue(1)

            filterIsOpen.setValue(false)
            filterIsActivate.setValue(false)
            filterSignatureStatuses.setValue([])
            filterDateNextCallFrom.setValue('')
            filterDateNextCallBefore.setValue('')
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        searchRef.current = txtSearch;
    }, [txtSearch])

    useEffect(() => {
        let applicationStatusesListSelected = ''

        filterSignatureStatuses.value.forEach((item) => {
            if (item.isSelected) {
                applicationStatusesListSelected += `'${item.name}',`
            }
        })

        filtersRef.current = {
            statuses: applicationStatusesListSelected,
            dateNextCallFrom: filterDateNextCallFrom.value,
            dateNextCallBefore: filterDateNextCallBefore.value,
        }
    }, [
        filterSignatureStatuses.value,
        filterDateNextCallFrom.value,
        filterDateNextCallBefore.value,
    ])

    useEffect(() => {
        loadPrimaryData(txtSearch)
    }, [paginationPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const unsubscribeSignature = realtimeService.subscribe('signature', () => {
            loadPrimaryData(searchRef.current, false)
        })

        intervalRef.current = setInterval(() => {
            loadPrimaryData(searchRef.current, false)
        }, 60000)

        return () => {
            unsubscribeSignature()
            clearInterval(intervalRef.current!)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps


    // ================================================================================
    

    /** Кнопка применить (очистить) в окне - Фильтр по заявкам */
    const handleApplyClearFilter = (filterType: 'apply' | 'clear') => {
        switch (filterType) {
            case 'apply': {
                // Фильтр активен только если значения отличаются от начальных
                const isFilterActive = 
                    filterSignatureStatuses.value.some(s => s.isSelected) ||
                    filterDateNextCallFrom.value !== '' ||
                    filterDateNextCallBefore.value !== ''
                filterIsActivate.setValue(isFilterActive)
                loadPrimaryData(txtSearch)
                break
            }

            case 'clear': {
                filterIsActivate.setValue(false)
                filterSignatureStatuses.setValue(filterSignatureStatuses.value.map(status => ({...status, isSelected: false})))
                filterDateNextCallFrom.setValue('')
                filterDateNextCallBefore.setValue('')

                actionGetPrimaryList(paginationPageNumber.value, txtSearch).then(() => {
                    filterIsActivate.setValue(false)
                })
                break
            }
        }
    }

    /** Открытие/закрытие анкеты договора */
    const handleOpenCardSignature = (isOpen: boolean, cardID?: number, contractNumber?: string) => {
        cardSignature.setValue({
            isOpen, cardID, contractNumber,
            handleButtonCancel() {cardSignature.setValue({isOpen: false})}, 
            handleButtonOk(isClosed) {
                if (isClosed) cardSignature.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }

    /** Отправка информации в telegram (ИКТ - Договора на подпись) */
    const handleSendToTelegram = (contractID: number) => {
        actionPostTgSignature(Number(contractID)).then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        })
    }

    // ================================================================================
    
    /** Свойства передаваемые в компоненту */
    const propsToComponent: ISignaturePage = {
        isAccessDenied, isLoadingPage, txtSearch, 
        filterIsOpen, filterIsActivate, paginationPageNumber,

        primaryList, primaryListPageSize, 
        primaryListTotalCount, primaryListNew, primaryListDelayedCall, 
        primaryListExecution, primaryListCanceled, primaryListNotClient,
        primaryListIgnore, 

        setTxtSearch, loadPrimaryData,
        handleApplyClearFilter, handleOpenCardSignature, handleSendToTelegram,
    }

    /** Свойства передаваемые в окно фильтра */
    const propsToFilter: IFilterSignature = {
        isOpen: filterIsOpen.value, 

        filterSignatureStatuses: filterSignatureStatuses, 
        filterDateNextCallFrom: filterDateNextCallFrom, 
        filterDateNextCallBefore: filterDateNextCallBefore,

        handleApplyClearFilter,
        handleButtonCancel: () => filterIsOpen.setValue(false),
    }

    return <>
        <SignaturePage {...propsToComponent} />
        <SignatureFilter {...propsToFilter} />

        <CardSignatureContainer {...cardSignature.value} />
    </>
}